// ---------------------------------------------------------------------------
// Sourcing preview curation
//
// The wizard's Apollo sourcing preview is editable: the operator can remove
// people they don't want and load more candidates. A removal must actually
// BIND — the removed apolloPersonIds are persisted on the draft and, at launch,
// campaign-prepare seeds them into its exclude set so the campaign is sourced
// around them (see removedApolloPersonIdsFromDraft on the server). These pure
// helpers hold the list arithmetic so it can be tested without the component.
// ---------------------------------------------------------------------------

/** Anything carrying an Apollo person id — the sourced-lead shape the list holds. */
export interface WithApolloPersonId {
  apolloPersonId?: string;
}

/**
 * The list to render: page-1 results plus everything "load more" has appended,
 * with removed people dropped and duplicates (by apolloPersonId) collapsed to
 * the first occurrence. Order is preserved. Leads without an apolloPersonId are
 * kept as-is (they can't be removed or deduped, but sourced leads always carry
 * one).
 */
export function curatedSourcedLeads<L extends WithApolloPersonId>(
  pageOne: L[],
  extra: L[],
  removedIds: Iterable<string>,
): L[] {
  const removed = new Set(removedIds);
  const seen = new Set<string>();
  const out: L[] = [];
  for (const lead of [...pageOne, ...extra]) {
    const id = lead.apolloPersonId;
    if (id) {
      if (removed.has(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(lead);
  }
  return out;
}

/**
 * Read the persisted removed set off a draft's apolloSearchInput. Defensive: the
 * draft is client-authored JSON, so anything that isn't a non-empty string is
 * dropped. Mirrors the server's removedApolloPersonIdsFromDraft.
 */
export function readRemovedApolloPersonIds(input: unknown): string[] {
  const raw = (input as Record<string, unknown> | null | undefined)?.removedApolloPersonIds;
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
}

/** Union several id lists into one deduped list, dropping empty ids. */
export function unionExcludeIds(...lists: Iterable<string>[]): string[] {
  const set = new Set<string>();
  for (const list of lists) for (const id of list) if (id) set.add(id);
  return [...set];
}

/** apolloPersonIds already fetched (page-1 + appended), for excluding on the next page. */
export function fetchedApolloPersonIds(pageOne: WithApolloPersonId[], extra: WithApolloPersonId[]): string[] {
  const set = new Set<string>();
  for (const lead of [...pageOne, ...extra]) if (lead.apolloPersonId) set.add(lead.apolloPersonId);
  return [...set];
}
